import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/api';
import type { Database } from '@/types/database.generated';
import type { AssessmentSchemes } from '@/lib/referral/assessmentSchemes';

export type {
  AssessmentFlagKey,
  AssessmentRespondentGroup,
  AssessmentSchemeItem,
  AssessmentSchemes,
} from '@/lib/referral/assessmentSchemes';
export {
  ASSESSMENT_RESPONDENT_GROUPS,
  ASSESSMENT_SCHEME_ITEMS,
  applyAssessmentToggle,
  assessmentSchemeItemKey,
  assessmentSchemeItemTitle,
  findAssessmentRespondentGroup,
  isAssessmentParentOn,
  isAssessmentRespondentOn,
  isAssessmentSlotRequested,
} from '@/lib/referral/assessmentSchemes';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ReferralStatus = Database['public']['Enums']['referral_status'];
type ReferralRow = Database['public']['Tables']['referrals']['Row'];

export interface PPRDocuments {
  institution_name?: string;
  wisc_v_forside?: boolean;
  wisc_v_rapport?: boolean;
  wppsi_iv_forside?: boolean;
  wppsi_iv_rapport?: boolean;
  andet?: boolean;
  andet_items?: string[];
}

export interface HospitalDocuments {
  institution_name?: string;
  journaludskrift?: boolean;
  scannings_svar?: boolean;
  scannings_svar_mr?: boolean;
  scannings_svar_ct?: boolean;
  scannings_svar_andet?: boolean;
  scannings_svar_andet_items?: string[];
  andet?: boolean;
  andet_items?: string[];
}

export interface OtherDocuments {
  requested_items?: string;
}

export interface PaperAnamneseForms {
  smaaboern?: boolean;
  smaaboern_genhenvisning?: boolean;
  skoleboern?: boolean;
  skoleboern_genhenvisning?: boolean;
  unge_15_plus?: boolean;
  unge_15_plus_genhenvisning?: boolean;
  tillaeg_til_anamnese_13_18?: boolean;
  nordisk_sporgeskema_2_5?: boolean;
  dupaul_foraeldre_skole?: boolean;
}

export interface SchoolCompletionForms {
  førskole?: boolean;
  indskoling?: boolean;
  mellemtrin?: boolean;
  udskoling?: boolean;
}

export interface DocumentsToSend {
  anamnese?: {
    paper?: boolean;
    electronic?: boolean;
  };
  consent_forms?: {
    smaaboern?: boolean;
    smaaboern_genhenvisning?: boolean;
    skoleboern?: boolean;
    skoleboern_genhenvisning?: boolean;
    unge_15_plus?: boolean;
    unge_genhenvisning?: boolean;
    tillaegs_til_anamnese_13_18?: boolean;
    nordisk_sporgeskema?: boolean;
    dupaul_førskole_skole?: boolean;
  };
  completion_scheme?: {
    førskole?: boolean;
    indskoling?: boolean;
    mellemtrin?: boolean;
    udskoling?: boolean;
  };
}

export type ReferralSource = 'manual' | 'medcom';

export interface ReferralListItem {
  id: string;
  clinic_id: string;
  referrer_name: string | null;
  referrer_type: string | null;
  referrer_reference: string | null;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  reason: string | null;
  notes: string | null;
  received_at: string;
  status: ReferralStatus;
  patient_id: string | null;
  assigned_clinician_id: string | null;
  has_cpr: boolean;
  consent_collected: boolean;
  info_letter_sent: boolean;
  agreement_made: boolean;
  payment_arranged: boolean;
  secretary_name: string | null;
  psychologist_name: string | null;
  id_number: string | null;
  case_number: string | null;
  ppr_documents: PPRDocuments;
  hospital_documents: HospitalDocuments;
  other_documents: OtherDocuments;
  documents_to_send: DocumentsToSend;
  assessment_schemes: AssessmentSchemes;
  paper_anamnese_forms: PaperAnamneseForms;
  school_completion_forms: SchoolCompletionForms;
  sender_hospital: string | null;
  sender_department: string | null;
  sent_at: string | null;
  referring_doctor_sor: string | null;
  referring_doctor_name: string | null;
  parent_1_name: string | null;
  has_parent_1_cpr: boolean;
  parent_2_name: string | null;
  has_parent_2_cpr: boolean;
  referral_diagnosis: string | null;
  ambulant: boolean | null;
  requested_examination: string | null;
  anamnese: string | null;
  created_at: string;
  updated_at: string;
  source: ReferralSource;
  medcom_exchange_id: string | null;
}

export interface ReferralPatientAddress {
  streetName?: string;
  suburbName?: string;
  districtName?: string;
  postCodeIdentifier?: string;
}

export interface ReferralPatientPhone {
  telephoneSubscriberIdentifier?: string;
  telephoneSubscriberCode?: string;
}

export interface ReferralAttachment {
  refDescription?: string;
  url?: string;
  sup?: boolean;
  objectIdentifier?: string;
  objectCode?: string;
  objectExtensionCode?: string;
  originalObjectSize?: string;
}

export interface ReferralAnswerCcReceiver {
  identifier?: string;
  identifierCode?: string;
  organisationName?: string;
  departmentName?: string;
  unitName?: string;
  streetName?: string;
  suburbName?: string;
  districtName?: string;
  postCodeIdentifier?: string;
  telephoneSubscriberIdentifier?: string;
}

export interface Referral extends ReferralListItem {
  created_by_user_id: string | null;
  patient_job_title: string | null;
  patient_address: ReferralPatientAddress;
  patient_phones: ReferralPatientPhone[];
  relative_relation_code: string | null;
  referral_diagnosis_code: string | null;
  cave: string | null;
  examination: string | null;
  actual_medicine: string | null;
  attachments: ReferralAttachment[];
  sender_ean: string | null;
  answer_cc_receiver: ReferralAnswerCcReceiver;
  episode_of_care_identifier: string | null;
  source: ReferralSource;
  medcom_exchange_id: string | null;
  medcom_letter_identifier: string | null;
  medcom_version_code: string | null;
  /** XREF06 / XREF01 for imported referrals (C2), else null. */
  medcom_type_code: string | null;
  /** XREF01 fields without a column (C2). Empty object otherwise. */
  medcom_details: Record<string, unknown>;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readOptionalString(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function readNullableText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return value.trim().length > 0 ? value : null;
}

function readReferralSource(value: unknown): ReferralSource {
  return value === 'medcom' ? 'medcom' : 'manual';
}

function readPatientAddress(value: unknown): ReferralPatientAddress {
  if (!isPlainRecord(value)) return {};
  const address: ReferralPatientAddress = {};
  const streetName = readOptionalString(value.streetName);
  const suburbName = readOptionalString(value.suburbName);
  const districtName = readOptionalString(value.districtName);
  const postCodeIdentifier = readOptionalString(value.postCodeIdentifier);
  if (streetName) address.streetName = streetName;
  if (suburbName) address.suburbName = suburbName;
  if (districtName) address.districtName = districtName;
  if (postCodeIdentifier) address.postCodeIdentifier = postCodeIdentifier;
  return address;
}

function readPatientPhones(value: unknown): ReferralPatientPhone[] {
  if (!Array.isArray(value)) return [];
  const phones: ReferralPatientPhone[] = [];
  for (const item of value) {
    if (!isPlainRecord(item)) continue;
    const phone: ReferralPatientPhone = {};
    const telephoneSubscriberIdentifier = readOptionalString(item.telephoneSubscriberIdentifier);
    const telephoneSubscriberCode = readOptionalString(item.telephoneSubscriberCode);
    if (telephoneSubscriberIdentifier) {
      phone.telephoneSubscriberIdentifier = telephoneSubscriberIdentifier;
    }
    if (telephoneSubscriberCode) phone.telephoneSubscriberCode = telephoneSubscriberCode;
    if (telephoneSubscriberIdentifier || telephoneSubscriberCode) phones.push(phone);
  }
  return phones;
}

function readAttachments(value: unknown): ReferralAttachment[] {
  if (!Array.isArray(value)) return [];
  const attachments: ReferralAttachment[] = [];
  for (const item of value) {
    if (!isPlainRecord(item)) continue;
    const attachment: ReferralAttachment = {};
    const refDescription = readOptionalString(item.refDescription);
    const url = readOptionalString(item.url);
    const objectIdentifier = readOptionalString(item.objectIdentifier);
    const objectCode = readOptionalString(item.objectCode);
    const objectExtensionCode = readOptionalString(item.objectExtensionCode);
    const originalObjectSize = readOptionalString(item.originalObjectSize);
    if (refDescription) attachment.refDescription = refDescription;
    if (url) attachment.url = url;
    if (item.sup === true) attachment.sup = true;
    if (objectIdentifier) attachment.objectIdentifier = objectIdentifier;
    if (objectCode) attachment.objectCode = objectCode;
    if (objectExtensionCode) attachment.objectExtensionCode = objectExtensionCode;
    if (originalObjectSize) attachment.originalObjectSize = originalObjectSize;
    attachments.push(attachment);
  }
  return attachments;
}

function readAnswerCcReceiver(value: unknown): ReferralAnswerCcReceiver {
  if (!isPlainRecord(value)) return {};
  const receiver: ReferralAnswerCcReceiver = {};
  const identifier = readOptionalString(value.identifier);
  const identifierCode = readOptionalString(value.identifierCode);
  const organisationName = readOptionalString(value.organisationName);
  const departmentName = readOptionalString(value.departmentName);
  const unitName = readOptionalString(value.unitName);
  const streetName = readOptionalString(value.streetName);
  const suburbName = readOptionalString(value.suburbName);
  const districtName = readOptionalString(value.districtName);
  const postCodeIdentifier = readOptionalString(value.postCodeIdentifier);
  const telephoneSubscriberIdentifier = readOptionalString(value.telephoneSubscriberIdentifier);
  if (identifier) receiver.identifier = identifier;
  if (identifierCode) receiver.identifierCode = identifierCode;
  if (organisationName) receiver.organisationName = organisationName;
  if (departmentName) receiver.departmentName = departmentName;
  if (unitName) receiver.unitName = unitName;
  if (streetName) receiver.streetName = streetName;
  if (suburbName) receiver.suburbName = suburbName;
  if (districtName) receiver.districtName = districtName;
  if (postCodeIdentifier) receiver.postCodeIdentifier = postCodeIdentifier;
  if (telephoneSubscriberIdentifier) {
    receiver.telephoneSubscriberIdentifier = telephoneSubscriberIdentifier;
  }
  return receiver;
}

// Generated ReferralRow predates the XREF06 columns. select('*') still returns
// them on the row, so the mapper reads those properties through this cast.
function readMedcomReferralColumns(row: ReferralRow): Pick<
  Referral,
  | 'patient_job_title'
  | 'patient_address'
  | 'patient_phones'
  | 'relative_relation_code'
  | 'referral_diagnosis_code'
  | 'cave'
  | 'examination'
  | 'actual_medicine'
  | 'attachments'
  | 'sender_ean'
  | 'answer_cc_receiver'
  | 'episode_of_care_identifier'
  | 'source'
  | 'medcom_exchange_id'
  | 'medcom_letter_identifier'
  | 'medcom_version_code'
  | 'medcom_type_code'
  | 'medcom_details'
> {
  const extra = row as ReferralRow & Record<string, unknown>;
  return {
    patient_job_title: readNullableText(extra.patient_job_title),
    patient_address: readPatientAddress(extra.patient_address),
    patient_phones: readPatientPhones(extra.patient_phones),
    relative_relation_code: readNullableText(extra.relative_relation_code),
    referral_diagnosis_code: readNullableText(extra.referral_diagnosis_code),
    cave: readNullableText(extra.cave),
    examination: readNullableText(extra.examination),
    actual_medicine: readNullableText(extra.actual_medicine),
    attachments: readAttachments(extra.attachments),
    sender_ean: readNullableText(extra.sender_ean),
    answer_cc_receiver: readAnswerCcReceiver(extra.answer_cc_receiver),
    episode_of_care_identifier: readNullableText(extra.episode_of_care_identifier),
    source: readReferralSource(extra.source),
    medcom_exchange_id: readNullableText(extra.medcom_exchange_id),
    medcom_letter_identifier: readNullableText(extra.medcom_letter_identifier),
    medcom_version_code: readNullableText(extra.medcom_version_code),
    medcom_type_code: readNullableText(extra.medcom_type_code),
    medcom_details: isPlainRecord(extra.medcom_details) ? extra.medcom_details : {},
  };
}

function mapReferralRow(row: ReferralRow): Referral {
  return {
    id: row.id,
    clinic_id: row.clinic_id,
    referrer_name: row.referrer_name,
    referrer_type: row.referrer_type,
    referrer_reference: row.referrer_reference,
    first_name: row.first_name,
    last_name: row.last_name,
    phone: row.phone,
    email: row.email,
    reason: row.reason,
    notes: row.notes,
    received_at: row.received_at,
    status: row.status as ReferralStatus,
    patient_id: row.patient_id,
    assigned_clinician_id: row.assigned_clinician_id,
    has_cpr: row.encrypted_cpr !== null && row.encrypted_cpr !== undefined,
    consent_collected: row.consent_collected,
    info_letter_sent: row.info_letter_sent,
    agreement_made: row.agreement_made,
    payment_arranged: row.payment_arranged,
    secretary_name: row.secretary_name,
    psychologist_name: row.psychologist_name,
    id_number: row.id_number,
    case_number: row.case_number,
    ppr_documents: (row.ppr_documents as PPRDocuments) ?? {},
    hospital_documents: (row.hospital_documents as HospitalDocuments) ?? {},
    other_documents: (row.other_documents as OtherDocuments) ?? {},
    documents_to_send: (row.documents_to_send as DocumentsToSend) ?? {},
    assessment_schemes: (row.assessment_schemes as AssessmentSchemes) ?? {},
    paper_anamnese_forms: (row.paper_anamnese_forms as PaperAnamneseForms) ?? {},
    school_completion_forms: (row.school_completion_forms as SchoolCompletionForms) ?? {},
    sender_hospital: row.sender_hospital,
    sender_department: row.sender_department,
    sent_at: row.sent_at,
    referring_doctor_sor: row.referring_doctor_sor,
    referring_doctor_name: row.referring_doctor_name,
    parent_1_name: row.parent_1_name,
    has_parent_1_cpr: row.parent_1_encrypted_cpr !== null && row.parent_1_encrypted_cpr !== undefined,
    parent_2_name: row.parent_2_name,
    has_parent_2_cpr: row.parent_2_encrypted_cpr !== null && row.parent_2_encrypted_cpr !== undefined,
    referral_diagnosis: row.referral_diagnosis,
    ambulant: row.ambulant,
    requested_examination: row.requested_examination,
    anamnese: row.anamnese,
    created_at: row.created_at,
    updated_at: row.updated_at,
    created_by_user_id: row.created_by_user_id,
    ...readMedcomReferralColumns(row),
  };
}

export type ReferralAssignmentFilter = 'all' | 'assigned' | 'unassigned';

export interface ReferralListFilters {
  status?: ReferralStatus | 'all';
  assignment?: ReferralAssignmentFilter;
  search?: string;
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const referralsListQueryKey = (clinicId: string | null, filters: ReferralListFilters) =>
  ['referrals', 'list', clinicId, filters] as const;

export const referralDetailQueryKey = (referralId: string | null | undefined) =>
  ['referrals', 'detail', referralId] as const;

export const referralIdByPatientQueryKey = (patientId: string | null | undefined) =>
  ['referrals', 'id-by-patient', patientId] as const;

// ── useReferralsList ──────────────────────────────────────────────────────────

export function useReferralsList(
  clinicId: string | null | undefined,
  filters: ReferralListFilters,
) {
  return useQuery({
    queryKey: referralsListQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ReferralListItem[]> => {
      if (!clinicId) return [];

      let q = supabase
        .from('referrals')
        .select(
          `id, clinic_id, referrer_name, referrer_type, referrer_reference,
           first_name, last_name, phone, email, reason, notes, received_at, status,
           patient_id, assigned_clinician_id, encrypted_cpr,
           consent_collected, info_letter_sent, agreement_made, payment_arranged,
           secretary_name, psychologist_name, id_number, case_number,
           ppr_documents, hospital_documents, other_documents, documents_to_send, assessment_schemes,
           paper_anamnese_forms, school_completion_forms,
           sender_hospital, sender_department, sent_at,
           referring_doctor_sor, referring_doctor_name,
           parent_1_name, parent_1_encrypted_cpr, parent_2_name, parent_2_encrypted_cpr,
           referral_diagnosis, ambulant, requested_examination, anamnese,
           created_at, updated_at, created_by_user_id,
           source, medcom_exchange_id`,
        )
        .eq('clinic_id', clinicId)
        .order('received_at', { ascending: false })
        .limit(500);

      if (filters.status && filters.status !== 'all') {
        q = q.eq('status', filters.status);
      }

      if (filters.assignment === 'unassigned') {
        q = q.is('assigned_clinician_id', null);
      } else if (filters.assignment === 'assigned') {
        q = q.not('assigned_clinician_id', 'is', null);
      }

      const search = filters.search?.trim();
      if (search) {
        q = q.or(
          `first_name.ilike.%${search}%,last_name.ilike.%${search}%,referrer_name.ilike.%${search}%`,
        );
      }

      const { data, error } = await q;
      if (error) throw error;

      // Generated referrals Row omits source and medcom_exchange_id, so naming
      // them in select() types each row as an error. Both columns are requested.
      return (data ?? []).map((row) => mapReferralRow(row as unknown as ReferralRow));
    },
    staleTime: 15_000,
  });
}

// ── useReferral ───────────────────────────────────────────────────────────────

export function useReferral(referralId: string | null | undefined) {
  return useQuery({
    queryKey: referralDetailQueryKey(referralId ?? null),
    enabled: Boolean(referralId),
    queryFn: async (): Promise<Referral> => {
      if (!referralId) throw new Error('No referral id');
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('id', referralId)
        .single();
      if (error) throw error;
      return mapReferralRow(data as ReferralRow);
    },
    staleTime: 5_000,
  });
}

/** Resolves the most recent referral id linked to a patient (after accept). */
export function useReferralIdByPatient(patientId: string | null | undefined) {
  return useQuery({
    queryKey: referralIdByPatientQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<string | null> => {
      if (!patientId) return null;
      const { data, error } = await supabase
        .from('referrals')
        .select('id')
        .eq('patient_id', patientId)
        .order('received_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.id ?? null;
    },
    staleTime: 5_000,
  });
}

export const patientReferralLettersQueryKey = (patientId: string | null | undefined) =>
  ['referrals', 'letters-by-patient', patientId] as const;

/** A referral linked to a patient, with the fields of its "Henvisnings detaljer". */
export interface PatientReferralLetter {
  id: string;
  received_at: string;
  referrer_name: string | null;
  sender_hospital: string | null;
  sender_department: string | null;
  referral_diagnosis: string | null;
  requested_examination: string | null;
  anamnese: string | null;
}

/** All referrals linked to a patient (RLS-scoped like /henvisninger), newest first. */
export function usePatientReferralLetters(patientId: string | null | undefined) {
  return useQuery({
    queryKey: patientReferralLettersQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientReferralLetter[]> => {
      if (!patientId) return [];
      const { data, error } = await supabase
        .from('referrals')
        .select(
          'id, received_at, referrer_name, sender_hospital, sender_department, referral_diagnosis, requested_examination, anamnese',
        )
        .eq('patient_id', patientId)
        .order('received_at', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PatientReferralLetter[];
    },
    staleTime: 5_000,
  });
}

// ── useCreateReferral ─────────────────────────────────────────────────────────

export interface CreateReferralInput extends Record<string, unknown> {
  clinic_id: string;
  referrer_name?: string;
  referrer_type?: string;
  referrer_reference?: string;
  first_name: string;
  last_name: string;
  cpr?: string;
  phone?: string;
  email?: string;
  reason?: string;
  notes?: string;
  secretary_name?: string;
  psychologist_name?: string;
  id_number?: string;
  case_number?: string;
  ppr_documents?: PPRDocuments;
  hospital_documents?: HospitalDocuments;
  other_documents?: OtherDocuments;
  documents_to_send?: DocumentsToSend;
  assessment_schemes?: AssessmentSchemes;
  paper_anamnese_forms?: PaperAnamneseForms;
  school_completion_forms?: SchoolCompletionForms;
  sender_hospital?: string;
  sender_department?: string;
  sent_at?: string;
  referring_doctor_sor?: string;
  referring_doctor_name?: string;
  parent_1_name?: string;
  parent_1_cpr?: string;
  parent_2_name?: string;
  parent_2_cpr?: string;
  referral_diagnosis?: string;
  ambulant?: boolean | null;
  requested_examination?: string;
  anamnese?: string;
}

export interface CreateReferralOutput {
  id: string;
  clinic_id: string;
  first_name: string;
  last_name: string;
  has_cpr: boolean;
  status: ReferralStatus;
  received_at: string;
}

export function useCreateReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateReferralInput): Promise<CreateReferralOutput> => {
      return await callFunction<CreateReferralInput, CreateReferralOutput>(
        'create-referral',
        input,
      );
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['referrals', 'list', row.clinic_id] });
    },
  });
}

// ── useAcceptReferral ─────────────────────────────────────────────────────────

export interface AcceptReferralInput extends Record<string, unknown> {
  referral_id: string;
}

export interface AcceptReferralOutput {
  referral_id: string;
  patient_id: string;
}

export function useAcceptReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AcceptReferralInput): Promise<AcceptReferralOutput> => {
      return await callFunction<AcceptReferralInput, AcceptReferralOutput>(
        'accept-referral',
        input,
      );
    },
    onSuccess: (result, vars) => {
      qc.invalidateQueries({ queryKey: referralDetailQueryKey(vars.referral_id) });
      qc.invalidateQueries({ queryKey: ['referrals', 'list'] });
      qc.invalidateQueries({ queryKey: ['patients', 'list'] });
      qc.invalidateQueries({ queryKey: referralIdByPatientQueryKey(result.patient_id) });
    },
  });
}

// ── useUpdateReferral ─────────────────────────────────────────────────────────

export interface UpdateReferralInput extends Record<string, unknown> {
  referral_id: string;
  referrer_name?: string | null;
  referrer_type?: string | null;
  referrer_reference?: string | null;
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  email?: string | null;
  reason?: string | null;
  notes?: string | null;
  cpr?: string | null;
  consent_collected?: boolean;
  info_letter_sent?: boolean;
  agreement_made?: boolean;
  payment_arranged?: boolean;
  status?: ReferralStatus;
  secretary_name?: string | null;
  psychologist_name?: string | null;
  id_number?: string | null;
  case_number?: string | null;
  ppr_documents?: PPRDocuments;
  hospital_documents?: HospitalDocuments;
  other_documents?: OtherDocuments;
  documents_to_send?: DocumentsToSend;
  assessment_schemes?: AssessmentSchemes;
  paper_anamnese_forms?: PaperAnamneseForms;
  school_completion_forms?: SchoolCompletionForms;
  sender_hospital?: string | null;
  sender_department?: string | null;
  sent_at?: string | null;
  referring_doctor_sor?: string | null;
  referring_doctor_name?: string | null;
  parent_1_name?: string | null;
  parent_1_cpr?: string | null;
  parent_2_name?: string | null;
  parent_2_cpr?: string | null;
  referral_diagnosis?: string | null;
  ambulant?: boolean | null;
  requested_examination?: string | null;
  anamnese?: string | null;
}

export interface UpdateReferralOutput {
  id: string;
  clinic_id: string;
  referrer_name: string | null;
  referrer_type: string | null;
  referrer_reference: string | null;
  first_name: string;
  last_name: string;
  has_cpr: boolean;
  phone: string | null;
  email: string | null;
  reason: string | null;
  notes: string | null;
  received_at: string;
  status: ReferralStatus;
  patient_id: string | null;
  assigned_clinician_id: string | null;
  consent_collected: boolean;
  info_letter_sent: boolean;
  agreement_made: boolean;
  payment_arranged: boolean;
  secretary_name: string | null;
  psychologist_name: string | null;
  id_number: string | null;
  case_number: string | null;
  ppr_documents: PPRDocuments;
  hospital_documents: HospitalDocuments;
  other_documents: OtherDocuments;
  documents_to_send: DocumentsToSend;
  assessment_schemes: AssessmentSchemes;
  paper_anamnese_forms: PaperAnamneseForms;
  school_completion_forms: SchoolCompletionForms;
  sender_hospital: string | null;
  sender_department: string | null;
  sent_at: string | null;
  referring_doctor_sor: string | null;
  referring_doctor_name: string | null;
  parent_1_name: string | null;
  has_parent_1_cpr: boolean;
  parent_2_name: string | null;
  has_parent_2_cpr: boolean;
  referral_diagnosis: string | null;
  ambulant: boolean | null;
  requested_examination: string | null;
  anamnese: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export function useUpdateReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateReferralInput): Promise<UpdateReferralOutput> => {
      console.log('[useUpdateReferral] Sending:', input);
      const result = await callFunction<UpdateReferralInput, UpdateReferralOutput>(
        'update-referral',
        input,
      );
      console.log('[useUpdateReferral] Received:', result);
      return result;
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await qc.cancelQueries({ queryKey: referralDetailQueryKey(variables.referral_id) });
      
      // Snapshot previous value
      const previousReferral = qc.getQueryData<Referral>(referralDetailQueryKey(variables.referral_id));
      
      // Optimistically update cache with proper merging
      qc.setQueryData<Referral>(referralDetailQueryKey(variables.referral_id), (old) => {
        if (!old) return old;
        
        // Remove referral_id from variables (it's not part of Referral type)
        const { referral_id, ...updateFields } = variables;
        
        // Merge update fields into old, preserving id
        return {
          ...old,
          ...updateFields,
          id: old.id,
        } as Referral;
      });
      
      return { previousReferral };
    },
    onError: (_err, variables, context: any) => {
      // Rollback on error
      if (context?.previousReferral) {
        qc.setQueryData(referralDetailQueryKey(variables.referral_id), context.previousReferral);
      }
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['referrals', 'list'] });
      qc.invalidateQueries({ queryKey: referralDetailQueryKey(vars.referral_id) });
    },
  });
}

// ── useAssignReferral ─────────────────────────────────────────────────────────

export interface AssignReferralInput extends Record<string, unknown> {
  referral_id: string;
  clinician_id: string | null;
}

export interface AssignReferralOutput {
  id: string;
  clinic_id: string;
  assigned_clinician_id: string | null;
}

export function useAssignReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssignReferralInput): Promise<AssignReferralOutput> => {
      return await callFunction<AssignReferralInput, AssignReferralOutput>(
        'assign-referral',
        input,
      );
    },
    onSuccess: (row, vars) => {
      qc.invalidateQueries({ queryKey: ['referrals', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: referralDetailQueryKey(vars.referral_id) });
    },
  });
}

// ── MedCom import ─────────────────────────────────────────────────────────────

export interface MedcomReferralExchange {
  exchange_id: string;
  letter_identifier: string | null;
  sender_ean: string | null;
  imported_at: string | null;
  already_imported: boolean;
  referral_id: string | null;
  /** C2: XREF06 or XREF01. */
  kind?: string;
  /** C20: loads of this same message (sender + kuvert + brev); shown once. Missing before 17.10.2026. */
  received_count?: number;
  /** C15: attachments the message announces, and how many MEDBINs have arrived. */
  attachment_count?: number;
  attachments_received?: number;
}

/** C13: inbound MEDBIN whose original message has not arrived yet. */
export interface MedcomWaitingAttachment {
  exchange_id: string;
  kind: string;
  received_at: string | null;
  object_identifier: string;
  object_code: string | null;
  object_extension_code: string | null;
  original_object_size: number | null;
  size_matches: boolean | null;
}

export interface MedcomReferralImportList {
  exchanges: MedcomReferralExchange[];
  truncated: boolean;
  waiting_attachments?: MedcomWaitingAttachment[];
}

export interface ListMedcomReferralsInput extends Record<string, unknown> {
  action: 'list';
  clinic_id: string;
}

export interface ImportMedcomReferralInput extends Record<string, unknown> {
  action: 'import';
  clinic_id: string;
  exchange_id: string;
}

export interface ImportMedcomReferralOutput {
  id: string;
  already_existed: boolean;
  /** Brev-ID of the referral returned (C20 hardening). */
  letter_identifier?: string | null;
}

export const medcomReferralImportQueryKey = (clinicId: string | null) =>
  ['referrals', 'medcom-import', clinicId] as const;

export function useMedcomReferralExchanges(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: medcomReferralImportQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<MedcomReferralImportList> => {
      if (!clinicId) return { exchanges: [], truncated: false };
      return await callFunction<ListMedcomReferralsInput, MedcomReferralImportList>(
        'communicate-referral-import',
        { action: 'list', clinic_id: clinicId },
      );
    },
  });
}

// ── C15: attachment status per MedCom referral (Henvisninger list) ───────────

export type ReferralAttachmentSummary = { referral_id: string; total: number; received: number };

export function useReferralAttachmentSummary(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: ['referrals', 'attachment-summary', clinicId ?? null] as const,
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<Map<string, ReferralAttachmentSummary>> => {
      if (!clinicId) return new Map();
      const out = await callFunction<
        { action: 'summary'; clinic_id: string },
        { referrals: ReferralAttachmentSummary[] }
      >('communicate-referral-attachments', { action: 'summary', clinic_id: clinicId });
      return new Map(out.referrals.map((r) => [r.referral_id, r]));
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useImportMedcomReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ImportMedcomReferralInput): Promise<ImportMedcomReferralOutput> => {
      return await callFunction<ImportMedcomReferralInput, ImportMedcomReferralOutput>(
        'communicate-referral-import',
        input,
      );
    },
    onSuccess: (result, vars) => {
      qc.setQueryData<MedcomReferralImportList>(
        medcomReferralImportQueryKey(vars.clinic_id),
        (current) => {
          if (!current) return current;
          return {
            ...current,
            exchanges: current.exchanges.map((row) => {
              if (row.exchange_id !== vars.exchange_id) return row;
              // C20: link a row only to the referral of its own letter (sender +
              // kuvert + brev). A mismatch is left to the server's list below.
              const mismatch =
                Boolean(result.letter_identifier) &&
                Boolean(row.letter_identifier) &&
                result.letter_identifier !== row.letter_identifier;
              return { ...row, already_imported: true, referral_id: mismatch ? null : result.id };
            }),
          };
        },
      );
      qc.invalidateQueries({ queryKey: ['referrals', 'list', vars.clinic_id] });
      qc.invalidateQueries({ queryKey: ['referrals', 'attachment-summary', vars.clinic_id] });
      // Re-read the server's message → referral mapping so every row links to
      // the referral made from its own letter.
      qc.invalidateQueries({ queryKey: medcomReferralImportQueryKey(vars.clinic_id) });
    },
  });
}

// ── useRevealReferralCpr (C4) ─────────────────────────────────────────────────
// One-shot reveal of a CPR stored on a received referral. Same rights and
// audit as 'Patient-CPR: Se' (decrypt-referral-cpr → cpr_decrypt_audit).

export type ReferralCprSubject = 'patient' | 'parent_1' | 'parent_2';

export type RevealReferralCprInput = {
  referral_id: string;
  subject: ReferralCprSubject;
  source: string;
  purpose?: string;
};

export function useRevealReferralCpr() {
  return useMutation({
    mutationFn: async (input: RevealReferralCprInput): Promise<{ cpr: string }> => {
      return await callFunction<RevealReferralCprInput, { cpr: string }>('decrypt-referral-cpr', input);
    },
  });
}

// ── Received referral attachments (C5) ────────────────────────────────────────
// XBIN01/XBIN02 coupled to the referral's <Reference> on ObjectIdentifier
// (communicate-referral-attachments).

export type ReceivedReferralAttachment = {
  object_identifier: string;
  received: boolean;
  exchange_id: string | null;
  kind: string | null;
  received_at: string | null;
  object_code: string | null;
  object_extension_code: string | null;
  original_object_size: number | null;
  actual_size: number | null;
  size_matches: boolean | null;
  file_name: string | null;
  mime_type: string | null;
};

export type ReceivedReferralAttachmentFile = {
  exchange_id: string;
  file_name: string;
  mime_type: string;
  size: number;
  base64: string;
};

export function useReceivedReferralAttachments(referralId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['referral-received-attachments', referralId ?? null],
    enabled: Boolean(referralId) && enabled,
    queryFn: async (): Promise<ReceivedReferralAttachment[]> => {
      const r = await callFunction<{ action: 'list'; referral_id: string }, { attachments: ReceivedReferralAttachment[] }>(
        'communicate-referral-attachments',
        { action: 'list', referral_id: referralId as string },
      );
      return r.attachments;
    },
    staleTime: 30_000,
    retry: false,
  });
}

/** C7: the received letter's Receiver block, read from the stored MedCom message (no CPR). */
export type ReferralLetterReceiver = {
  ean: string | null;
  identifier: string | null;
  identifier_code: string | null;
  organisation_name: string | null;
  department_name: string | null;
  unit_name: string | null;
  street_name: string | null;
  suburb_name: string | null;
  district_name: string | null;
  post_code: string | null;
};

export async function fetchReferralLetterReceiver(referralId: string): Promise<ReferralLetterReceiver | null> {
  const r = await callFunction<{ action: 'receiver'; referral_id: string }, { receiver: ReferralLetterReceiver | null }>(
    'communicate-referral-attachments',
    { action: 'receiver', referral_id: referralId },
  );
  return r.receiver;
}

export function useReferralLetterReceiver(referralId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['referral-letter-receiver', referralId ?? null],
    enabled: Boolean(referralId) && enabled,
    queryFn: async () => await fetchReferralLetterReceiver(referralId as string),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export async function fetchReceivedReferralAttachments(referralId: string): Promise<ReceivedReferralAttachment[]> {
  const r = await callFunction<{ action: 'list'; referral_id: string }, { attachments: ReceivedReferralAttachment[] }>(
    'communicate-referral-attachments',
    { action: 'list', referral_id: referralId },
  );
  return r.attachments;
}

export async function fetchReceivedReferralAttachment(
  referralId: string,
  exchangeId: string,
  objectIdentifier?: string | null,
): Promise<ReceivedReferralAttachmentFile> {
  return await callFunction<
    { action: 'download'; referral_id: string; exchange_id: string; object_identifier?: string },
    ReceivedReferralAttachmentFile
  >('communicate-referral-attachments', {
    action: 'download',
    referral_id: referralId,
    exchange_id: exchangeId,
    // One XBIN01 may carry several objects (REF01-M 3.1: JPG + PDF).
    ...(objectIdentifier ? { object_identifier: objectIdentifier } : {}),
  });
}
